import { useCallback, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Option, Zone } from 'types';

import { useDuplicateOrderMutation, useGetOrderByIdQuery } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { setOrderId, setOrderNumber, setZone } from 'redux/slices/settings';
import { getUserZonesWithSpecial } from 'redux/slices/userData';

import { getApiErrorMessage } from 'helpers/getApiErrorMessage';

import { routes } from 'data/routes';

import { ORDER_ADDRESS_FIELDS, shownAddresses } from 'components/OrderAddressBlocks';

type UseDuplicateOrderParams = {
  orderId: string;
  customerCode: string;
  customerName?: string;
  onClose: () => void;
};

type DuplicateOrderResult = {
  orderId: string;
  orderNumber: string;
  zone: string;
};

const useDuplicateOrder = (params: UseDuplicateOrderParams) => {
  const { orderId, customerCode, customerName, onClose } = params;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const zones = useAppSelector(getUserZonesWithSpecial);
  const globalZone = useAppSelector((state) => state.settings.zone);

  const { control, setValue, handleSubmit } = useForm<FieldValues>({
    defaultValues: {
      zone: globalZone || '',
      [ORDER_ADDRESS_FIELDS.customer]: customerCode || '',
    },
  });

  const [selectedZone, selectedZoneSet] = useState<string>(globalZone || '');
  const [isLoading, isLoadingSet] = useState<boolean>(false);
  const [isPrefilling, isPrefillingSet] = useState<boolean>(false);
  const [successResult, successResultSet] = useState<DuplicateOrderResult | null>(null);

  const [duplicateOrder] = useDuplicateOrderMutation();

  // The source order's Shipping / Goods are shown (and sent) unless the user changes them.
  const { currentData: sourceOrder, isLoading: isSourceLoading } =
    useGetOrderByIdQuery(orderId);
  const initialAddresses = useMemo(
    () =>
      sourceOrder
        ? {
            shippingAddressCode: sourceOrder.shippingAddressCode,
            goodsDestinationCode: sourceOrder.goodsDestinationCode,
          }
        : null,
    [sourceOrder]
  );
  const isPreparing = isSourceLoading || isPrefilling;

  const zoneOptions: Option[] = useMemo(
    () => zones.map((z: Zone) => ({ value: z.code, label: z.code })),
    [zones]
  );

  // The address picker drops the customer (and its addresses) when the zone changes.
  const handleZoneChange = useCallback((selected: Option) => {
    selectedZoneSet(String(selected.value));
  }, []);

  const initialCustomer = useMemo(
    () => (customerCode ? { code: customerCode, name: customerName } : null),
    [customerCode, customerName]
  );

  const handleConfirm = useCallback(
    async (data: FieldValues) => {
      const customer = data[ORDER_ADDRESS_FIELDS.customer];
      if (!selectedZone || !customer || isPreparing) return;

      isLoadingSet(true);
      try {
        // No season: the copy always takes the source order's own season (the backend refuses another one),
        // and the season selected at the top of the app can differ from it.
        const result = await duplicateOrder({
          orderId,
          zone: selectedZone,
          customerCode: String(customer),
          // Always both, as shown ('' = none): never rely on the BE keeping the source ones.
          ...shownAddresses(data),
        }).unwrap();

        successResultSet({
          orderId: result.orderId,
          orderNumber: String(result.orderNumber),
          zone: result.zone,
        });
      } catch (e: unknown) {
        toast.error(getApiErrorMessage(e, 'Order duplication failed, please try again'));
      } finally {
        isLoadingSet(false);
      }
    },
    [orderId, selectedZone, isPreparing, duplicateOrder]
  );

  const handleGoToOrder = useCallback(() => {
    if (!successResult) return;

    dispatch(setZone(successResult.zone));
    localStorage.setItem('zone', successResult.zone);

    dispatch(setOrderId(successResult.orderId));
    dispatch(setOrderNumber(successResult.orderNumber));
    localStorage.setItem('orderId', successResult.orderId);
    localStorage.setItem('orderNumber', successResult.orderNumber);

    onClose();
    navigate(routes.singleOrder(successResult.orderId));
  }, [successResult, dispatch, onClose, navigate]);

  const handleStayOnPage = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = handleSubmit(handleConfirm);

  return {
    control,
    setValue,
    zoneOptions,
    selectedZone,
    initialCustomer,
    initialAddresses,
    isPrefillingSet,
    isLoading,
    isPreparing,
    globalZone,
    successResult,
    handleZoneChange,
    handleGoToOrder,
    handleStayOnPage,
    onSubmit,
  };
};

export { useDuplicateOrder };
